import { Component, ElementRef, Input, Optional } from '@angular/core';
import { Select } from '@ngxs/store';
import { Observable, Subscription } from 'rxjs';
import { PageObjectService } from '@Core/models/proprietary/page/page-object.service';
import {
    DesignerComponent,
    DesignerComponentType,
    DesignerElement,
    PageState,
    PageView
} from '@Core/models/proprietary/page/page.state';
import { AppService } from '@Core/services/proprietary/app.service';
import { FormGroupMapping } from '@Core/services/proprietary/object-rule/object-rule.types';
import { SubPanelComponent } from '../sub-panel/sub-panel.component';

@Component({
    standalone: false,
    selector: 'ep-ux-container',
    template: `
    <ng-container *ngIf="app.DesignerMode && (currentPageView$ | async)?.Layout_Object_Code != 'CORE'">
      <!-- btn btn-sm button-default margin-l-5 -->
      <unity-alert *ngIf="unnamedComponents" type="danger" style="margin-top:5px">
        One or more block, section, group, or field elements are not named. Field reordering will be disabled. Contact
        Insurity support.
      </unity-alert>
      <div style="width:20px;">
        <!-- This above (surrounding) div is a hack to get tooltip to show up over the gear.  
         Th unity button container was filling the panel width -->
        <button unity-button
          *ngIf="!uxConfigMode"
          (click)="uxConfigMode = true"
          class="uxContainerButton"
          classes="icon-only icon-only-large"
          ngbTooltip="Configure Edit Panel"
        >
          <svg class="unity-icon unity-icon-cog" style="fill:orange">
            <use xlink:href="#unity-icon-cog"></use>
          </svg>
        </button>
      </div>
      <div *ngIf="uxConfigMode" class="uxDesignerEditorPanel">
        <span class="uxDesignerEditorTitle">Edit Panel Designer</span>Set entry field rules and order
        <div style="margin-top: 3px">
          <ep-ux-designer-template-panel
            (onSelectSetting)="selectedSettingID = $event"
            style="vertical-align:top"
          ></ep-ux-designer-template-panel>
          <ng-container *ngIf="templateSelected">
            <button unity-button
              classes="secondary"
              (click)="toggleShowHidden()"
              [disabled]="!uxConfigEditMode"
              ngbTooltip="Show/Hide Hidden Fields"
            >
              {{ this.app.DesignerModeShowHidden ? 'Hide Hidden' : 'Show Hidden' }}
            </button>
            <button unity-button
              classes="secondary"
              (click)="uxConfigReset('base-layout')"
              [disabled]="!uxConfigEditMode"
              ngbTooltip="Reset Field Layout Order for this Edit Panel"
            >
              Reset Layout
            </button>
            <button unity-button
              classes="secondary"
              (click)="uxConfigReset('base-rules')"
              [disabled]="!uxConfigEditMode"
              ngbTooltip="Reset all Rules for this Edit Panel"
            >
              Reset Rules
            </button>
            <button unity-button
              classes="tertiary"
              (click)="uxConfigReset('saved')"
              [disabled]="!uxConfigEditMode"
              ngbTooltip="Discard changes to edit panel settings"
            >
              Cancel
            </button>
            <button unity-button
              class="primary"
              (click)="uxConfigSave()"
              [disabled]="!uxConfigEditMode"
              ngbTooltip="Accept changes to edit panel settings"
            >
              Accept
            </button>
          </ng-container>
          <button unity-button
            *ngIf="!uxConfigEditMode && !templateSelected"
            classes="tertiary"
            (click)="uxConfigSave()"
            ngbTooltip="Close Edit Panel Designer"
          >
            Close
          </button>
        </div>
      </div>
    </ng-container>
    <!-- research code - never implemented
      cdkDropList
      (cdkDropListDropped)="drop($event)"
    -->
    <div
      class="uxContainerContent"
      [class.uxContainerLayoutMode]="uxConfigMode"
      [class.uxConfigEditModeOff]="!uxConfigEditMode"
    >
      <ng-content></ng-content>
    </div>
  `
})
export class UXContainerComponent {
    @Select(PageState.currentPageView)
    currentPageView$: Observable<PageView>;

    //is name the right property to identify a rule by?    
    // What should the structure of a name ep-section be  Section_Lease?  Section.Lease?  Upper/lower? camel?
    @Input()
    name: string = null;

    @Input()
    baseChildLayout: any = {};

    @Input()
    formGroupMapping: FormGroupMapping[];

    private _uxConfigMode: boolean = false;
    private _uxConfigEditMode: boolean = false;
    selectedSettingID: string = null;

    @Select(PageState.currentDesignerComponent)
    currentDesignerComponent$: Observable<DesignerComponent>;
    currentDesignerComponentSub: Subscription;

    private _unnamedFieldBlockCounter = 0;
    private _unnamedFieldSectionCounter = 0;
    private _unnamedFieldGroupCounter = 0;
    private _unnamedFieldCounter = 0;

    unnamedFieldBlockAutoNumber() {
        this._unnamedFieldBlockCounter++;
        return ('000' + this._unnamedFieldBlockCounter).slice(-3); //zerofill to 3 places
    }

    unnamedFieldSectionAutoNumber() {
        this._unnamedFieldSectionCounter++;
        return ('000' + this._unnamedFieldSectionCounter).slice(-3); //zerofill to 3 places
    }
    unnamedFieldGroupAutoNumber() {
        this._unnamedFieldGroupCounter++;
        return ('000' + this._unnamedFieldGroupCounter).slice(-3); //zerofill to 3 places
    }
    unnamedFieldAutoNumber() {
        this._unnamedFieldCounter++;
        return ('000' + this._unnamedFieldCounter).slice(-3); //zerofill to 3 places
    }
    //END HACK f9d3ec9c-eac2-4e96-a484-7b3bae1f949e

    get unnamedComponents() {
        return (
            this._unnamedFieldBlockCounter +
            this._unnamedFieldSectionCounter +
            this._unnamedFieldGroupCounter +
            this._unnamedFieldCounter >
            0
        );
    }

    get templateSelected() {
        if (
            this.selectedSettingID &&
            this.selectedSettingID != '' &&
            this.selectedSettingID != 'null' &&
            this.selectedSettingID != '0'
        ) {
            return true;
        }
        return false;
    }

    constructor(
        public element: ElementRef,
        public app: AppService,
        public pageOS: PageObjectService,
        @Optional() public subPanelComponent: SubPanelComponent
    ) { }

    public get uxConfigMode(): boolean {
        return this._uxConfigMode;
    }

    public set uxConfigMode(value: boolean) {
        this._uxConfigMode = value;
        //note:  If this component later wraps more than just edit panel fields, we will need to derive the component type from the name
        if (value) {
            this.pageOS.setCurrentDesignerComponent({
                name: this.name,
                type: DesignerComponentType.EditPanel,
                formGroupMapping: this.formGroupMapping,
                currentElement: null,
                baseChildLayout: this.baseChildLayout
            });
            this.currentDesignerComponentSub = this.currentDesignerComponent$.subscribe((x) => {
                this._uxConfigEditMode = x && x.editMode == true;
                if (!this._uxConfigEditMode) {
                    this.app.designerClearElementSelection();
                }
            });
        } else {
            this.pageOS.setCurrentDesignerComponent(null);
            if (this.currentDesignerComponentSub) {
                this.currentDesignerComponentSub.unsubscribe();
            }
        }
    }

    public get uxConfigEditMode(): boolean {
        return this._uxConfigEditMode;
    }

    // used by descendent elements (ep-field, etc.)
    public set currentDesignerElement(value: DesignerElement) {
        this.pageOS.setCurrentDesignerElement(value);
    }

    uxConfigClose() {
        this.app.DesignerModeShowHidden = false;
        this.app.designerClearElementSelection();
        this.uxConfigMode = false;
    }

    public uxConfigSave() {
        this.uxConfigClose();
    }

    public uxConfigSelectTemplate() {
        alert(
            // eslint-disable-next-line max-len
            'Not Implemented!  This will be replaced with a dropdown to select a saved template for this edit panel.  Also need ability to manage saved templates.  Name, SaveAs, Delete, List Page Layouts that use the template, etc.'
        );
    }

    public uxConfigReset(mode: 'saved' | 'base-rules' | 'base-layout') {
        if (mode == 'base-rules') {
            this.pageOS.updateSectionUxConfigComponentSetting(null, this.name, 'RuleSet', null);
        }
        if (mode == 'base-layout') {
            this.pageOS.updateSectionUxConfigComponentSetting(null, this.name, 'ChildLayout', null);
        }
        if (mode == 'saved') {
            this.pageOS.restoreSectionUxConfigComponentSetting(null, this.name, 'All');
            this.uxConfigClose();
        }
    }

    toggleShowHidden() {
        this.app.DesignerModeShowHidden = !this.app.DesignerModeShowHidden;
    }
}
